import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, X, Hourglass, FilePenLine } from "lucide-react";
import { toast } from "sonner";

const fmtHora = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" }) : "—";

/** Bandeja admin: solicitudes de consumo de horas + correcciones de fichaje del Portal del empleado. */
export function SolicitudesPortalAdmin({ isReadOnly, onChanged }) {
  const [horas, setHoras] = useState([]);
  const [correcciones, setCorrecciones] = useState([]);

  const load = useCallback(async () => {
    try {
      const [h, c] = await Promise.all([
        api.get("/portal-empleado/solicitudes-horas", { params: { estado: "pendiente" } }),
        api.get("/fichajes/correcciones", { params: { estado: "pendiente" } }),
      ]);
      setHoras(h.data || []); setCorrecciones(c.data || []);
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolverHoras = async (s, aprobar) => {
    const comentario = window.prompt(aprobar ? "Comentario (opcional)" : "Motivo del rechazo (opcional)") ?? null;
    try {
      await api.patch(`/portal-empleado/solicitudes-horas/${s.id}/resolver`, { aprobar, comentario_admin: comentario || null });
      toast.success(aprobar ? "Consumo aprobado · ausencia y movimiento creados" : "Solicitud rechazada");
      load(); onChanged && onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };
  const resolverCorreccion = async (c, aprobar) => {
    const comentario = window.prompt(aprobar ? "Comentario (opcional)" : "Motivo del rechazo (opcional)") ?? null;
    try {
      await api.patch(`/fichajes/correcciones/${c.id}`, { aprobar, comentario: comentario || null });
      toast.success(aprobar ? "Corrección aplicada y jornada recalculada" : "Corrección rechazada");
      load(); onChanged && onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const Acciones = ({ onOk, onKo, id }) => isReadOnly ? <span className="text-[10px] text-zinc-500 italic">Solo lectura</span> : (
    <div className="flex gap-1.5">
      <Button size="sm" onClick={onOk} className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs" data-testid={`aprobar-${id}`}><Check className="w-3 h-3 mr-1" /> Aprobar</Button>
      <Button size="sm" variant="outline" onClick={onKo} className="bg-transparent border-red-700 text-red-300 hover:bg-red-500/10 h-7 text-xs" data-testid={`rechazar-${id}`}><X className="w-3 h-3 mr-1" /> Rechazar</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-zinc-900/30 border border-indigo-500/20 rounded-lg overflow-hidden" data-testid="bandeja-horas">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-indigo-200/80 border-b border-zinc-800 flex items-center gap-2">
          <Hourglass className="w-3.5 h-3.5" /> Consumo de horas acumuladas · pendientes ({horas.length})
        </div>
        {horas.length === 0 ? <div className="text-center py-5 text-zinc-500 text-sm">Sin solicitudes de horas pendientes</div> : (
          <div className="divide-y divide-zinc-800/60">
            {horas.map((s) => (
              <div key={s.id} className="p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`sol-horas-${s.id}`}>
                <div className="flex-1 min-w-0 text-sm">
                  <span className="text-zinc-100 font-medium">{s.user_nombre}</span>
                  <span className="text-xs text-zinc-500 font-mono ml-2">{s.fecha}</span>
                  <span className="text-xs text-indigo-200 ml-2">−{s.horas} h {s.modo === "dia" ? "(día completo)" : "(parcial)"}</span>
                  {s.motivo && <div className="text-xs text-zinc-400 mt-0.5">"{s.motivo}"</div>}
                  <div className="text-[10px] text-zinc-500 mt-0.5">Se aplicaría FIFO a: {(s.aplicacion_prevista || []).map((a) => a.periodo ? `${a.periodo} (${a.horas} h)` : `SIN COBERTURA ${a.horas} h`).join(", ") || "—"}</div>
                </div>
                <Acciones id={s.id} onOk={() => resolverHoras(s, true)} onKo={() => resolverHoras(s, false)} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-zinc-900/30 border border-amber-500/20 rounded-lg overflow-hidden" data-testid="bandeja-correcciones">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-amber-200/80 border-b border-zinc-800 flex items-center gap-2">
          <FilePenLine className="w-3.5 h-3.5" /> Correcciones de fichaje · pendientes ({correcciones.length})
        </div>
        {correcciones.length === 0 ? <div className="text-center py-5 text-zinc-500 text-sm">Sin correcciones pendientes</div> : (
          <div className="divide-y divide-zinc-800/60">
            {correcciones.map((c) => (
              <div key={c.id} className="p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`sol-corr-${c.id}`}>
                <div className="flex-1 min-w-0 text-sm">
                  <span className="text-zinc-100 font-medium">{c.user_nombre}</span>
                  <span className="text-xs text-zinc-500 font-mono ml-2">{c.fecha_laboral || ""}</span>
                  <span className="text-xs text-amber-300 ml-2">{c.accion || (c.nuevo_fichaje ? "crear" : "modificar")} {c.tipo_propuesto || ""} {c.timestamp_propuesto ? `→ ${fmtHora(c.timestamp_propuesto)}` : ""}</span>
                  {c.fichaje_snapshot && <span className="text-[10px] text-zinc-500 ml-2">(original: {c.fichaje_snapshot.tipo} {fmtHora(c.fichaje_snapshot.timestamp)})</span>}
                  <div className="text-xs text-zinc-400 mt-0.5">{c.motivo_tipo ? `[${c.motivo_tipo}] ` : ""}"{c.motivo}"</div>
                </div>
                <Acciones id={c.id} onOk={() => resolverCorreccion(c, true)} onKo={() => resolverCorreccion(c, false)} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
